import {
	isVendorConsented as isVendorConsentedGPP,
	waitForVendorConsent,
} from "@trinitymirrordigital/consent-helper/gpp";
import {
	isNonIABVendorConsented,
	waitForNonIABVendorConsent,
} from "@trinitymirrordigital/consent-helper/inmobi-noniab";
import {
	isVendorConsented as isVendorConsentedTCF,
	waitForVendorConsent as waitForConsentTCF,
} from "@trinitymirrordigital/consent-helper/tcf";
import type { BaseModule, ConsentModuleConfig } from "@/ts/module";
import { appendHtmlToHead } from "@/utils/dom";
import { getInMobiTag } from "@/vendors/InMobiTag";

export class Consent implements BaseModule {
	private config: ConsentModuleConfig;
	name = "Consent" as const;

	constructor(config: ConsentModuleConfig) {
		this.config = config;
	}

	async init(): Promise<void> {
		const { injectScriptTag, cmpDomain } = this.config;
		if (injectScriptTag && cmpDomain) {
			appendHtmlToHead(getInMobiTag(cmpDomain));
		}
		await Promise.resolve();
	}

	/**
	 * @param param0 Options object for consent check
	 *   - vendorId: get it/them from GVL, or InMobi NonIAB list
	 *   - isNonIAB: true for NonIAB Vendors. Defaults to false
	 *   - purposes: optional, get them from GVL. Defaults to all of the purposes.
	 *   - sync: use synchronous check. Defaults to false
	 * @returns boolean or Promise<boolean>
	 */
	run(args: {
		vendorId: number | number[];
		purposes?: number[];
		isNonIAB?: boolean;
		sync: true;
	}): boolean;
	run(args: {
		vendorId: number | number[];
		purposes?: number[];
		isNonIAB?: boolean;
		sync?: false;
	}): Promise<boolean>;
	run({
		vendorId,
		purposes,
		isNonIAB = false,
		sync = false,
	}: {
		vendorId: number | number[];
		purposes?: number[];
		isNonIAB?: boolean;
		sync?: boolean;
	}): boolean | Promise<boolean> {
		if (sync) {
			return isVendorConsentedSync(this.config, vendorId, isNonIAB, purposes);
		}
		return waitForVendorConsentAsync(this.config, vendorId, isNonIAB, purposes);
	}
}

const isVendorConsentedSync = (
	config: ConsentModuleConfig,
	vendorId: number | number[],
	isNonIAB: boolean,
	purposes?: number[],
): boolean => {
	if (isNonIAB) {
		return isNonIABVendorConsented(vendorId, purposes);
	}
	if (config.gpp && config.tcf) {
		return (
			isVendorConsentedGPP(vendorId, purposes) &&
			isVendorConsentedTCF(vendorId, purposes)
		);
	}
	if (config.gpp) {
		return isVendorConsentedGPP(vendorId, purposes);
	}
	if (config.tcf) {
		return isVendorConsentedTCF(vendorId, purposes);
	}
	return true;
};

const waitForVendorConsentAsync = (
	config: ConsentModuleConfig,
	vendorId: number | number[],
	isNonIAB: boolean,
	purposes?: number[],
): Promise<boolean> => {
	if (isNonIAB) {
		return waitForNonIABVendorConsent(vendorId, purposes);
	}
	if (config.gpp && config.tcf) {
		return Promise.all([
			waitForVendorConsent(vendorId, purposes),
			waitForConsentTCF(vendorId, purposes),
		]).then((results) => results.every(Boolean));
	}
	if (config.gpp) {
		return waitForVendorConsent(vendorId, purposes);
	}
	if (config.tcf) {
		return waitForConsentTCF(vendorId, purposes);
	}
	return Promise.resolve(true);
};
